import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc, 
  writeBatch 
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Collection references
const PATIENTS_COLLECTION = 'patients';

// Type for Patient from the dashboard
interface Patient {
  id?: string;
  name: string;
  treatmentType: string;
  adherenceRate: number;
  missedDoses: number;
  confirmedDoses: number;
  pendingDoses: number;
  startDate: string;
  nextAppointment: string;
  municipality: string;
  province: string;
  age: number;
  sex: string;
  civilStatus: string;
  philHealthNumber: string;
  phoneNumber: string;
}

// Get all patients
export const getAllPatients = async (): Promise<Patient[]> => {
  try {
    const patientsCollection = collection(db, PATIENTS_COLLECTION);
    const snapshot = await getDocs(patientsCollection);
    
    return snapshot.docs.map(doc => ({ 
      id: doc.id, // id is guaranteed to exist here
      ...doc.data() 
    } as Patient));
  } catch (error) {
    console.error('Error getting patients:', error);
    throw error;
  }
};

// Add a new patient
export const addPatient = async (patient: Omit<Patient, 'id'>): Promise<string> => {
  try {
    const patientsCollection = collection(db, PATIENTS_COLLECTION);
    const docRef = await addDoc(patientsCollection, patient);
    return docRef.id;
  } catch (error) {
    console.error('Error adding patient:', error);
    throw error;
  }
};

// Update a patient
export const updatePatient = async (id: string, data: Partial<Patient>): Promise<void> => {
  try {
    const patientDoc = doc(db, PATIENTS_COLLECTION, id);
    await updateDoc(patientDoc, data);
  } catch (error) {
    console.error('Error updating patient:', error);
    throw error;
  }
};

// Delete a patient
export const deletePatient = async (id: string): Promise<void> => {
  try {
    const patientDoc = doc(db, PATIENTS_COLLECTION, id);
    await deleteDoc(patientDoc);
  } catch (error) {
    console.error('Error deleting patient:', error);
    throw error;
  }
};

// Get a specific patient
export const getPatient = async (id: string): Promise<Patient | null> => {
  try {
    const patientDoc = doc(db, PATIENTS_COLLECTION, id);
    const snapshot = await getDoc(patientDoc);
    
    if (snapshot.exists()) {
      return { id: snapshot.id, ...snapshot.data() } as Patient;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting patient:', error);
    throw error;
  }
};

// Bulk import patients
export const bulkImportPatients = async (patients: Omit<Patient, 'id'>[]): Promise<void> => {
  try {
    // Step 1: Get all existing patients to check for duplicates
    const patientsCollection = collection(db, PATIENTS_COLLECTION);
    const snapshot = await getDocs(patientsCollection);
    const existingPatients = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Patient[];

    // Create a batch operation
    const batch = writeBatch(db);
    
    // Process each patient in the import
    for (const patient of patients) {
      // Check if this patient already exists (match by name and philHealthNumber)
      const existingPatient = existingPatients.find(
        existing => 
          existing.name === patient.name && 
          existing.philHealthNumber === patient.philHealthNumber
      );
      
      if (existingPatient && existingPatient.id) {
        // Patient exists - update the existing document
        const patientRef = doc(patientsCollection, existingPatient.id);
        batch.update(patientRef, patient);
        console.log(`Updating existing patient: ${patient.name}`);
      } else {
        // New patient - create a new document
        const newPatientRef = doc(collection(db, PATIENTS_COLLECTION));
        batch.set(newPatientRef, patient);
        console.log(`Adding new patient: ${patient.name}`);
      }
    }
    
    // Commit all the batch operations
    await batch.commit();
  } catch (error) {
    console.error('Error importing patients in bulk:', error);
    throw error;
  }
};

// Create a named service object for default export
const firestoreService = {
  getAllPatients,
  addPatient,
  updatePatient,
  deletePatient,
  getPatient,
  bulkImportPatients
};

export default firestoreService; 